import { ReactNode } from "react";
import { FactPopover } from "@/components/programs/FactPopover";

export type FactSegment =
  | { type: "text"; value: string }
  | { type: "ref"; factId: string; num: number };

/**
 * Extract all [FACT-xxx] from text, build a deduped ordered list,
 * and return the text with [FACT-xxx] replaced by numbered segments.
 */
export function parseFactReferences(text: string, factIds: string[] = []): {
  segments: FactSegment[];
  orderedFactIds: string[];
} {
  const inlineRefs: string[] = [];
  // Tolerant regex: matches [FACT-xxx], FACT-xxx, FACTxxx
  const regex = /\[FACT-([^\]]+)\]|(?<!\[)FACT-?([a-f0-9]{6,36})/gi;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    const id = match[1] || match[2];
    if (!inlineRefs.includes(id)) {
      inlineRefs.push(id);
    }
  }

  for (const fid of factIds) {
    const cleanFid = fid.replace(/^FACT-/i, "");
    if (!inlineRefs.includes(cleanFid)) {
      inlineRefs.push(cleanFid);
    }
  }

  const segments: FactSegment[] = [];
  let lastIndex = 0;
  const globalRegex = /\[FACT-([^\]]+)\]|(?<!\[)FACT-?([a-f0-9]{6,36})/gi;
  let m: RegExpExecArray | null;

  while ((m = globalRegex.exec(text)) !== null) {
    if (m.index > lastIndex) {
      segments.push({ type: "text", value: text.slice(lastIndex, m.index) });
    }
    const id = m[1] || m[2];
    const num = inlineRefs.indexOf(id) + 1;
    segments.push({ type: "ref", factId: id, num });
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) {
    segments.push({ type: "text", value: text.slice(lastIndex) });
  }

  return { segments, orderedFactIds: inlineRefs };
}

/**
 * Convert raw URLs inside a text string into short clickable anchors.
 */
function linkifyText(text: string): ReactNode[] {
  const urlRegex = /(https?:\/\/[^\s<>,;)"']+)/g;
  const parts: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = urlRegex.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(
      <a
        key={`link-${m.index}`}
        href={m[0]}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary underline underline-offset-2 hover:text-primary/80 transition-colors"
      >
        ici
      </a>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length > 0 ? parts : [text];
}

/**
 * Render parsed segments as React nodes with inline FactPopover badges.
 * Raw URLs in text segments are automatically converted to short anchors.
 */
export function renderFactSegments(segments: FactSegment[]): ReactNode[] {
  return segments.map((seg, i) =>
    seg.type === "text" ? (
      <span key={i}>{linkifyText(seg.value)}</span>
    ) : (
      <FactPopover key={`${seg.factId}-${i}`} factId={seg.factId} index={seg.num - 1}>
        <button className="inline-flex items-center justify-center ml-0.5 px-1.5 py-0 text-[9px] font-bold text-primary bg-primary/10 rounded-full cursor-pointer hover:bg-primary/20 transition-colors align-super leading-none border border-primary/20 hover:border-primary/40">
          {seg.num}
        </button>
      </FactPopover>
    )
  );
}
